/**
 * One tile of the Co-Living dashboard's KPI row (Slice 18).
 *
 * TOKENS ONLY. `bg-card`, `border-border`, `text-foreground`,
 * `text-muted-foreground` — no hex, so light mode works because the theme says
 * so rather than because `index.css` repaints every colour back. The accents
 * are the same emerald/amber/destructive idiom the Inventar 2.0 surfaces use.
 *
 * THE DOM SHAPE IS A CONTRACT, not a layout detail. Label, value and hint are
 * three ADJACENT SIBLINGS in that order, exactly as `SmallStatCard` and
 * `HeroCard` rendered them before this slice. Five existing safety nets read a
 * tile with `getByText(label).nextElementSibling` for the value and one more
 * `.nextElementSibling` for the hint; keeping the shape keeps roughly a hundred
 * assertions pointed at the numbers they were written for instead of at markup
 * that happens to have moved. Do not wrap the value in an extra element.
 *
 * A `null`/`undefined` value renders the operating dash, never a zero — the
 * dash-vs-zero rule this dashboard has carried since Slice 7. Zero is a
 * measurement; the dash is the absence of one, and they must not look alike.
 */
import React from "react";

/** Shown wherever a measurement would stand but none is available. */
export const KPI_DASH = "—";

/**
 * Accent per tile. Presentation only: which tone a KPI wears says nothing
 * about what it means, and no threshold is computed here.
 */
const TONES = {
  neutral: { tile: "bg-muted text-muted-foreground", value: "text-foreground" },
  blue: {
    tile: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
    value: "text-foreground",
  },
  violet: {
    tile: "bg-violet-500/10 text-violet-700 dark:text-violet-400",
    value: "text-foreground",
  },
  success: {
    tile: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    value: "text-emerald-700 dark:text-emerald-400",
  },
  danger: {
    tile: "bg-destructive/10 text-destructive",
    value: "text-destructive",
  },
};

/**
 * The ring beside "Auslastung", as in the reference.
 *
 * Pure SVG — no chart dependency for one circle. It is `aria-hidden` because
 * the same percentage is already in the tile's value text: a ring that repeats
 * a number is decoration, and announcing it twice would be noise. That also
 * satisfies the rule that nothing here may be understandable by colour alone.
 */
function OccupancyRing({ percent }) {
  const safe = Math.max(0, Math.min(100, Number(percent) || 0));
  const r = 17;
  const circumference = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 44 44" className="h-11 w-11 shrink-0" aria-hidden focusable="false">
      <circle
        cx="22"
        cy="22"
        r={r}
        fill="none"
        strokeWidth="5"
        className="stroke-muted"
      />
      <circle
        cx="22"
        cy="22"
        r={r}
        fill="none"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={`${(safe / 100) * circumference} ${circumference}`}
        transform="rotate(-90 22 22)"
        className="stroke-emerald-500"
      />
    </svg>
  );
}

/**
 * @param {string}      label   the KPI's name — also the handle every existing
 *                              safety net looks it up by.
 * @param {*}           value   already formatted; null/undefined → dash.
 * @param {string}      hint    the secondary line, third sibling.
 * @param {React.ElementType} icon  a lucide icon, rendered in the tinted tile.
 * @param {string}      tone    one of TONES.
 * @param {number|null} ringPercent  draws the occupancy ring when set.
 */
export default function CoLivingKpiCard({
  label,
  value,
  hint,
  icon: Icon = null,
  tone = "neutral",
  ringPercent = null,
}) {
  const t = TONES[tone] ?? TONES.neutral;
  const shown = value === null || value === undefined ? KPI_DASH : value;

  return (
    <div className="flex items-start gap-3 rounded-[10px] border border-border bg-card p-4">
      {Icon ? (
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] ${t.tile}`}
          aria-hidden
        >
          <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
        </span>
      ) : null}

      {/* label → value → hint, adjacent siblings. See the file header. */}
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-[11px] font-medium text-muted-foreground">
          {label}
        </p>
        <p className={`m-0 mt-0.5 text-[22px] font-semibold leading-tight ${t.value}`}>
          {shown}
        </p>
        <p className="m-0 mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</p>
      </div>

      {ringPercent === null || ringPercent === undefined ? null : (
        <OccupancyRing percent={ringPercent} />
      )}
    </div>
  );
}
